import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Drawer, Box, Typography, IconButton, Button, TextField,
  Paper, Grid, Select, MenuItem, FormControl, InputLabel, Checkbox,
  FormControlLabel, Stack, CircularProgress, Collapse, Switch, Alert,
  Chip, Avatar, ClickAwayListener,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { configApi } from '../../../services/configApi';
import { toSmallestUnit, fromPercent } from '../../../utils/configUtils';
import { allUserListApi } from '../../../api';
import configuration from '../../../config/Config';

const SHIP_METHODS = ['standard', 'express', 'two_day', 'next_day', 'pickup'];
const APPLY_TO_OPTS = ['subtotal', 'order_total'];

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'Heebo' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#065F46' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#065F46' },
};

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontFamily: 'Heebo', fontWeight: 600, color: '#362B24', opacity: 0.5,
    fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.5,
  }}>
    {children}
  </Typography>
);

const defaultAppFee = () => ({
  percentage: '5', flat: '0', apply_to: 'subtotal', min_fee: '0', max_fee: '0', is_active: true,
});
const defaultShipping = () => ({
  method: 'standard', base_cost: '0', free_above: '0', is_active: true,
});

function buildAppFeePayload(fee) {
  return {
    percentage: fromPercent(fee.percentage),
    flat: toSmallestUnit(fee.flat),
    apply_to: fee.apply_to,
    min_fee: toSmallestUnit(fee.min_fee),
    max_fee: toSmallestUnit(fee.max_fee),
    is_active: fee.is_active,
  };
}
function buildShippingPayload(shippings) {
  return shippings.map((s) => ({
    method: s.method,
    base_cost: toSmallestUnit(s.base_cost),
    free_above: toSmallestUnit(s.free_above),
    is_active: s.is_active,
  }));
}

/* ─────────────────────────────────────────────
   SellerSelect — multi-select with search + pagination
───────────────────────────────────────────── */
function SellerSelect({ selected, onChange, token, error }) {
  const [open, setOpen]           = useState(false);
  const [search, setSearch]       = useState('');
  const [vendors, setVendors]     = useState([]);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotal]    = useState(1);
  const [loading, setLoading]     = useState(false);
  const [loadingMore, setMore]    = useState(false);
  const debounceRef               = useRef(null);
  const anchorRef                 = useRef(null);

  const fetchVendors = useCallback(async (q, p, append = false) => {
    if (append) setMore(true); else setLoading(true);
    try {
      const res = await allUserListApi({ token, type: 2, currentPage: p, searchQuery: q });
      const list = res?.users || [];
      setVendors((prev) => append ? [...prev, ...list] : list);
      setTotal(res?.totalPages || 1);
      setPage(res?.currentPage || p);
    } catch {}
    if (append) setMore(false); else setLoading(false);
  }, [token]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchVendors(search, 1, false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, open, fetchVendors]);

  useEffect(() => {
    if (open) fetchVendors('', 1, false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (vendor) => {
    const already = selected.some((s) => s._id === vendor._id);
    onChange(already ? selected.filter((s) => s._id !== vendor._id) : [...selected, vendor]);
  };

  const removeChip = (id) => onChange(selected.filter((s) => s._id !== id));

  const loadMore = () => fetchVendors(search, page + 1, true);

  const getInitials = (name = '') =>
    name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box ref={anchorRef} sx={{ position: 'relative' }}>
        {/* Trigger box */}
        <Box
          onClick={() => setOpen((o) => !o)}
          sx={{
            minHeight: 42, px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer',
            border: `1px solid ${error ? '#d32f2f' : open ? '#065F46' : 'rgba(0,0,0,0.23)'}`,
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
            '&:hover': { borderColor: open ? '#065F46' : 'rgba(0,0,0,0.87)' },
          }}
        >
          {selected.length === 0 ? (
            <Typography sx={{ fontFamily: 'Heebo', fontSize: '14px', color: 'rgba(0,0,0,0.45)', flex: 1 }}>
              Select sellers…
            </Typography>
          ) : (
            selected.map((s) => (
              <Chip
                key={s._id}
                size="small"
                label={s.fullname || s.email || s._id}
                onDelete={(e) => { e.stopPropagation(); removeChip(s._id); }}
                sx={{ fontFamily: 'Heebo', fontSize: '12px', bgcolor: 'rgba(6,95,70,0.08)', color: '#065F46', '& .MuiChip-deleteIcon': { color: '#065F46' } }}
              />
            ))
          )}
          <KeyboardArrowDownOutlinedIcon
            sx={{ ml: 'auto', fontSize: 18, color: 'rgba(0,0,0,0.4)', transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
          />
        </Box>
        {error && (
          <Typography sx={{ fontSize: '11px', color: '#d32f2f', mt: 0.4, ml: 1.5 }}>{error}</Typography>
        )}

        {/* Dropdown */}
        {open && (
          <Paper
            elevation={4}
            sx={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 1400, borderRadius: '12px', overflow: 'hidden', border: '1px solid #f0f0f0' }}
          >
            {/* Search */}
            <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <TextField
                autoFocus
                size="small"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                fullWidth
                InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ fontSize: 16, color: 'rgba(0,0,0,0.4)', mr: 0.5 }} /> }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: 'Heebo', fontSize: '13px' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#065F46' },
                }}
              />
            </Box>

            {/* List */}
            <Box sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {loading ? (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={22} sx={{ color: '#065F46' }} />
                </Box>
              ) : vendors.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Heebo', fontSize: '13px', color: 'rgba(0,0,0,0.4)' }}>No sellers found</Typography>
                </Box>
              ) : vendors.map((v) => {
                const isChecked = selected.some((s) => s._id === v._id);
                return (
                  <Box
                    key={v._id}
                    onClick={(e) => { e.stopPropagation(); toggle(v); }}
                    sx={{
                      px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer',
                      bgcolor: isChecked ? 'rgba(6,95,70,0.05)' : 'transparent',
                      '&:hover': { bgcolor: isChecked ? 'rgba(6,95,70,0.08)' : '#fafafa' },
                      transition: 'background 0.12s',
                    }}
                  >
                    <Checkbox
                      checked={isChecked}
                      size="small"
                      sx={{ p: 0, color: 'rgba(0,0,0,0.25)', '&.Mui-checked': { color: '#065F46' } }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggle(v)}
                    />
                    <Avatar
                      src={v.picture ? `${configuration.cdnFront}/${v.picture}` : undefined}
                      sx={{ width: 30, height: 30, bgcolor: '#065F46', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}
                    >
                      {!v.picture && getInitials(v.fullname)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontFamily: 'Heebo', fontWeight: 600, fontSize: '13px', color: '#362B24', lineHeight: 1.3 }} noWrap>
                        {v.fullname || '—'}
                      </Typography>
                      <Typography sx={{ fontFamily: 'Heebo', fontSize: '11px', color: 'rgba(0,0,0,0.45)', lineHeight: 1.3 }} noWrap>
                        {v.email}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}

              {/* Load more */}
              {!loading && page < totalPages && (
                <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #f0f0f0' }}>
                  <Button
                    fullWidth
                    size="small"
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={12} color="inherit" /> : null}
                    onClick={(e) => { e.stopPropagation(); loadMore(); }}
                    sx={{ fontFamily: 'Heebo', textTransform: 'none', fontSize: '12px', color: '#065F46' }}
                  >
                    {loadingMore ? 'Loading…' : `Load more (page ${page + 1} of ${totalPages})`}
                  </Button>
                </Box>
              )}
            </Box>

            {/* Selection count */}
            {selected.length > 0 && (
              <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f0f0f0', bgcolor: 'rgba(6,95,70,0.04)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontFamily: 'Heebo', fontSize: '12px', color: '#065F46', fontWeight: 600 }}>
                  {selected.length} selected
                </Typography>
                <Button size="small" onClick={(e) => { e.stopPropagation(); setOpen(false); }} sx={{ fontFamily: 'Heebo', textTransform: 'none', fontSize: '12px', color: '#065F46' }}>
                  Done
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
}

/* ─────────────────────────────────────────────
   AppFeeFields / ShippingFields (unchanged helpers)
───────────────────────────────────────────── */
function AppFeeFields({ fee, onChange }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1, borderRadius: 1.5 }}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField label="Commission %" size="small" type="number" value={fee.percentage} onChange={(e) => onChange({ ...fee, percentage: e.target.value })} inputProps={{ step: '0.01', min: 0, max: 100 }} helperText="0–100" fullWidth sx={inputSx} />
        </Grid>
        <Grid item xs={6}>
          <TextField label="Flat Fee ($)" size="small" type="number" value={fee.flat} onChange={(e) => onChange({ ...fee, flat: e.target.value })} inputProps={{ step: '0.01', min: 0 }} fullWidth sx={inputSx} />
        </Grid>
        <Grid item xs={6}>
          <FormControl size="small" fullWidth sx={inputSx}>
            <InputLabel>Apply To</InputLabel>
            <Select value={fee.apply_to} label="Apply To" onChange={(e) => onChange({ ...fee, apply_to: e.target.value })}>
              {APPLY_TO_OPTS.map((v) => <MenuItem key={v} value={v} sx={{ fontFamily: 'Heebo' }}>{v}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField label="Min Fee ($)" size="small" type="number" value={fee.min_fee} onChange={(e) => onChange({ ...fee, min_fee: e.target.value })} inputProps={{ step: '0.01', min: 0 }} fullWidth sx={inputSx} />
        </Grid>
        <Grid item xs={6}>
          <TextField label="Max Fee ($)" size="small" type="number" value={fee.max_fee} onChange={(e) => onChange({ ...fee, max_fee: e.target.value })} inputProps={{ step: '0.01', min: 0 }} helperText="0 = no cap" fullWidth sx={inputSx} />
        </Grid>
        <Grid item xs={6}>
          <FormControlLabel control={<Checkbox checked={fee.is_active} onChange={(e) => onChange({ ...fee, is_active: e.target.checked })} sx={{ color: '#065F46', '&.Mui-checked': { color: '#065F46' } }} />} label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '13px' }}>Active</Typography>} />
        </Grid>
      </Grid>
    </Paper>
  );
}

function ShippingFields({ shippings, onChange }) {
  const update = (i, key, val) => onChange(shippings.map((s, idx) => idx === i ? { ...s, [key]: val } : s));
  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {shippings.map((s, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl size="small" fullWidth sx={inputSx}>
                <InputLabel>Method</InputLabel>
                <Select value={s.method} label="Method" onChange={(e) => update(i, 'method', e.target.value)}>
                  {SHIP_METHODS.map((v) => <MenuItem key={v} value={v} sx={{ fontFamily: 'Heebo' }}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel control={<Checkbox checked={s.is_active} onChange={(e) => update(i, 'is_active', e.target.checked)} sx={{ color: '#065F46', '&.Mui-checked': { color: '#065F46' } }} />} label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '13px' }}>Active</Typography>} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Base Cost ($)" size="small" type="number" value={s.base_cost} onChange={(e) => update(i, 'base_cost', e.target.value)} inputProps={{ step: '0.01', min: 0 }} fullWidth sx={inputSx} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Free Above ($)" size="small" type="number" value={s.free_above} onChange={(e) => update(i, 'free_above', e.target.value)} inputProps={{ step: '0.01', min: 0 }} helperText="0 = never free" fullWidth sx={inputSx} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button size="small" color="error" onClick={() => onChange(shippings.filter((_, idx) => idx !== i))} sx={{ fontFamily: 'Heebo', textTransform: 'none' }}>Remove</Button>
          </Box>
        </Paper>
      ))}
      <Button startIcon={<AddOutlinedIcon />} onClick={() => onChange([...shippings, defaultShipping()])} sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#065F46', alignSelf: 'flex-start' }}>
        Add Shipping
      </Button>
    </Stack>
  );
}

/* ─────────────────────────────────────────────
   WhitelistDrawer
───────────────────────────────────────────── */
export default function WhitelistDrawer({ open, mode, entry, onClose, onSuccess }) {
  const token = useSelector((state) => state?.user?.user?.token);

  const [selectedSellers, setSelectedSellers] = useState([]);
  const [note, setNote]                       = useState('');
  const [isActive, setIsActive]               = useState(true);
  const [overrideAppFee, setOverrideAppFee]   = useState(false);
  const [overrideShipping, setOverrideShipping] = useState(false);
  const [appFee, setAppFee]                   = useState(defaultAppFee());
  const [shippings, setShippings]             = useState([]);
  const [formError, setFormError]             = useState('');
  const [submitting, setSubmitting]           = useState(false);
  const [errors, setErrors]                   = useState({});

  useEffect(() => {
    if (!open) {
      setSelectedSellers([]);
      setNote('');
      setIsActive(true);
      setOverrideAppFee(false);
      setOverrideShipping(false);
      setAppFee(defaultAppFee());
      setShippings([]);
      setFormError('');
      setErrors({});
      return;
    }
    if (mode === 'edit' && entry) {
      setNote(entry.note || '');
      setIsActive(entry.is_active ?? true);
      if (entry.app_fee) {
        setOverrideAppFee(true);
        setAppFee({
          percentage: String((+(entry.app_fee.percentage ?? 0) * 100).toFixed(2).replace(/\.00$/, '')),
          flat: String((entry.app_fee.flat ?? 0) / 100),
          apply_to: entry.app_fee.apply_to || 'subtotal',
          min_fee: String((entry.app_fee.min_fee ?? 0) / 100),
          max_fee: String((entry.app_fee.max_fee ?? 0) / 100),
          is_active: entry.app_fee.is_active ?? true,
        });
      }
      if (entry.shipping?.length) {
        setOverrideShipping(true);
        setShippings(entry.shipping.map((s) => ({
          method: s.method,
          base_cost: String(s.base_cost / 100),
          free_above: String(s.free_above / 100),
          is_active: s.is_active,
        })));
      }
    }
  }, [open, mode, entry]);

  const handleSubmit = async () => {
    const errs = {};
    if (mode === 'add' && selectedSellers.length === 0) errs.sellers = 'Select at least one seller';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setFormError('');
    setSubmitting(true);

    try {
      if (mode === 'add') {
        const results = await Promise.allSettled(
          selectedSellers.map((s) =>
            configApi.addToWhitelist(token, {
              seller_id: s._id,
              note: note || undefined,
              app_fee: overrideAppFee ? buildAppFeePayload(appFee) : null,
              shipping: overrideShipping ? buildShippingPayload(shippings) : null,
            })
          )
        );
        const succeeded = results.filter((r) => r.status === 'fulfilled');
        const conflicts = results.filter((r) => r.status === 'rejected' && r.reason?.error?.toLowerCase().includes('already whitelisted'));
        const failed    = results.filter((r) => r.status === 'rejected' && !r.reason?.error?.toLowerCase().includes('already whitelisted'));

        if (succeeded.length > 0) toast.success(`${succeeded.length} seller${succeeded.length > 1 ? 's' : ''} added to whitelist.`);
        if (conflicts.length > 0) setFormError(`${conflicts.length} seller${conflicts.length > 1 ? 's' : ''} already whitelisted.`);
        if (failed.length > 0)    toast.error(`${failed.length} seller${failed.length > 1 ? 's' : ''} failed to add.`);
        if (succeeded.length > 0) onSuccess();
      } else {
        await configApi.updateWhitelist(token, entry.seller_id?._id || entry._id, {
          note,
          is_active: isActive,
          app_fee: overrideAppFee ? buildAppFeePayload(appFee) : null,
          shipping: overrideShipping ? buildShippingPayload(shippings) : null,
        });
        toast.success('Whitelist entry updated.');
        onSuccess();
      }
    } catch (e) {
      toast.error(e?.error || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = () => {
    if (submitting) return 'Saving…';
    if (mode === 'add') return selectedSellers.length > 1 ? `Add ${selectedSellers.length} Sellers` : 'Add Seller';
    return 'Save Changes';
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 520, maxWidth: '100vw' } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

        {/* Header */}
        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{ fontFamily: 'Heebo', fontWeight: 700, fontSize: '16px', color: '#362B24' }}>
            {mode === 'add' ? 'Add Whitelisted Sellers' : `Edit: ${entry?.seller_id?.name || 'Seller'}`}
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseOutlinedIcon fontSize="small" /></IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {formError && <Alert severity="warning" sx={{ fontFamily: 'Heebo' }}>{formError}</Alert>}

          {/* Seller select — add mode only */}
          {mode === 'add' && (
            <Box>
              <SectionLabel>Sellers *</SectionLabel>
              <SellerSelect
                selected={selectedSellers}
                onChange={setSelectedSellers}
                token={token}
                error={errors.sellers}
              />
            </Box>
          )}

          {/* Note + Active */}
          <Box>
            <SectionLabel>Details</SectionLabel>
            <Stack spacing={2}>
              <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} multiline rows={2} fullWidth size="small" sx={inputSx} />
              {mode === 'edit' && (
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#065F46' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#065F46' } }} />}
                  label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '14px' }}>Active</Typography>}
                />
              )}
            </Stack>
          </Box>

          {/* App Fee Override */}
          <Box>
            <FormControlLabel
              control={<Switch checked={overrideAppFee} onChange={(e) => setOverrideAppFee(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#065F46' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#065F46' } }} />}
              label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '14px', fontWeight: 500 }}>Override Platform App Fee</Typography>}
            />
            <Collapse in={overrideAppFee}>
              <AppFeeFields fee={appFee} onChange={setAppFee} />
            </Collapse>
          </Box>

          {/* Shipping Override */}
          <Box>
            <FormControlLabel
              control={<Switch checked={overrideShipping} onChange={(e) => setOverrideShipping(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#065F46' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#065F46' } }} />}
              label={<Typography sx={{ fontFamily: 'Heebo', fontSize: '14px', fontWeight: 500 }}>Override Platform Shipping</Typography>}
            />
            <Collapse in={overrideShipping}>
              <ShippingFields shippings={shippings} onChange={setShippings} />
            </Collapse>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
          <Button onClick={onClose} sx={{ fontFamily: 'Heebo', textTransform: 'none', color: '#362B24' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={handleSubmit}
            sx={{ bgcolor: '#065F46', fontFamily: 'Heebo', textTransform: 'none', borderRadius: '10px', minWidth: 130, '&:hover': { bgcolor: '#054d38' } }}
          >
            {submitLabel()}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
